import { useRef, useEffect, useState } from 'react';

interface SlideItem {
  type: 'image' | 'video';
  src: string;
  alt?: string;
}

const slides: SlideItem[] = [
  {
    type: 'video',
    src: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/banner.MP4',
    alt: '华东交通大学',
  },
];

export default function HeroBanner() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleLoaded = () => setVideoLoaded(true);
    // Check if video is already loaded (cached)
    if (video.readyState >= 2) {
      setVideoLoaded(true);
    } else {
      video.addEventListener('loadeddata', handleLoaded);
    }
    return () => video.removeEventListener('loadeddata', handleLoaded);
  }, []);

  return (
    <section className="relative w-full h-[40vh] xs:h-[45vh] sm:h-[55vh] md:h-[calc(100vh-132px)] overflow-hidden min-h-[280px] max-h-[900px] bg-primary-950">
      {/* ━━ Placeholder background while video loads ━━ */}
      <div
        className={`absolute inset-0 bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800 transition-opacity duration-700 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
      />

      {/* ━━ Background Video ━━ */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-700 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
          >
            {slide.type === 'video' ? (
              <video
                ref={videoRef}
                src={slide.src}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 30%' }}
                aria-label={slide.alt}
              />
            ) : (
              <img
                src={slide.src}
                alt={slide.alt || ''}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        ))}
      </div>

      {/* ━━ Sunlight effect from top-left ━━ */}
      <div
        className="absolute inset-0 pointer-events-none z-[5]"
        style={{
          background: 'radial-gradient(ellipse at 8% 12%, rgba(255,235,180,0.25) 0%, rgba(255,215,130,0.10) 30%, rgba(255,195,100,0.03) 55%, transparent 72%)',
        }}
      />

      {/* ━━ Dark vignette for text contrast ━━ */}
      <div
        className="absolute inset-0 z-[6] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.12) 55%, rgba(0,0,0,0.40) 100%)',
        }}
      />

      {/* ━━ Title Overlay (reserved for future text) ━━ */}
      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
        <div className="w-full max-w-[1800px] mx-auto px-4 md:px-8" />
      </div>

      {/* ━━ Bottom Fade to next section ━━ */}
      <div
        className="absolute bottom-0 left-0 right-0 pointer-events-none z-10"
        style={{
          height: 'clamp(40px, 10vw, 100px)',
          background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.97))',
        }}
      />
    </section>
  );
}
